import { wTextDO, wTextST } from '@/constants/wTextST';
import { memo, useEffect, useState, useCallback, useRef } from 'react';

function pad(number, width) {
	return new Array(+width + 1 - (number + '').length).join('0') + number;
}

const CountdownServing = memo(function CountdownServing({ expTime, whenExpired = () => {}, isDisable, disableOrder }) {
	const [countdownTime, setCountdownTime] = useState({
		cD: 0,
		cH: 0,
		cM: 0,
		cS: 0,
	});
	
	const intervalRef = useRef(null);

	// Calculate remaining time and update countdown
	const updateCountdown = useCallback(() => {
		if (!expTime) return;

		const now = new Date().getTime();
		const targetTime = new Date(expTime).getTime();
		const remaining = targetTime - now;

		if (remaining <= 0) {
			// Time expired
			setCountdownTime({ cD: 0, cH: 0, cM: 0, cS: 0 });
			if (!isDisable) {
				whenExpired(true);
			}
			return false; // Stop timer
		}

		// Calculate time units
		const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
		const hours = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
		const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
		const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

		setCountdownTime({
			cD: days,
			cH: hours,
			cM: minutes,
			cS: seconds,
		});

		return true; // Continue timer
	}, [expTime, isDisable, whenExpired]);

	// Start countdown timer
	const startTimer = useCallback(() => {
		if (!expTime || isDisable) return;

		// Clear any existing timer
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
		}

		// Initial update
		updateCountdown();

		// Start interval
		intervalRef.current = setInterval(() => {
			const shouldContinue = updateCountdown();
			if (!shouldContinue) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
		}, 1000);
	}, [expTime, isDisable, updateCountdown]);

	// Cleanup timer
	const clearTimer = useCallback(() => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}
	}, []);

	// Effect to manage timer lifecycle
	useEffect(() => {
		if (expTime && !isDisable) {
			startTimer();
		} else {
			clearTimer();
			setCountdownTime({ cD: 0, cH: 0, cM: 0, cS: 0 });
		}

		return clearTimer;
	}, [expTime, isDisable, startTimer, clearTimer]);

	// Update countdown when expTime changes
	useEffect(() => {
		if (expTime) {
			updateCountdown();
		}
	}, [expTime, updateCountdown]);

	const { cH, cM, cS, cD } = countdownTime;
	
	// Only show deadline when less than 12 hours remaining
	const showDeadline = expTime && !isDisable && (
		(cD === 0 && cH < 12) || // Less than 12 hours (same day)
		(cD === 0 && cH === 0 && cM > 0) || // Less than 1 hour
		(cD === 0 && cH === 0 && cM === 0 && cS > 0) // Less than 1 minute
	);
	
	const showWarning = !expTime || isDisable || disableOrder;
	
	// Format display text
	const getDisplayText = () => {
		if (cD > 0) {
			return `Ordering deadline in ${cD} ${cD > 1 ? 'days' : 'day'} ${pad(cH, 2)}:${pad(cM, 2)}:${pad(cS, 2)}`;
		}
		return `Ordering deadline in ${pad(cH, 2)}:${pad(cM, 2)}:${pad(cS, 2)}`;
	};

	return (
		<div className='flex flex-wrap items-center justify-end w-full gap-1'>
			{showWarning && (
				<span className='text-red-300'>
					{!disableOrder ? wTextST : wTextDO}
				</span>
			)}
			{showDeadline && (
				<span>
					{getDisplayText()}
				</span>
			)}
		</div>
	);
});

export default CountdownServing;
